package com.example.mystybag.login.logic

import com.example.mystybag.common.resources.SR
import com.example.mystybag.communication.Communication
import com.example.mystybag.communication.common.CommonCommands
import com.example.mystybag.communication.utils.Response
import com.example.mystybag.communication.utils.onResponse
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_IS_STAFF
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_MERCHANT_EMAIL
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_MERCHANT_ID
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_MERCHANT_JWT
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_MERCHANT_PASSWORD
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_STAFF_LOCATION_IDS
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_USE_BIOMETRIC_FOR_MERCHANT
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_MERCHANT_BIOMETRIC_PROMPT_DECLINED
import com.example.mystybag.core.expected.Preferences
import com.example.mystybag.core.expected.sharedActivity
import com.example.mystybag.core.expected.sharedString
import com.example.mystybag.core.security.Security
import com.example.mystybag.core.security.isJwtExpired
import com.example.mystybag.core.logic.base.AppContext
import com.example.mystybag.core.logic.base.ChildViewModel
import com.example.mystybag.core.logic.base.NavigatorConfig
import com.example.mystybag.biometricUtils.expected.BiometricsHandler
import com.example.mystybag.biometricUtils.helpers.BiometricAuthenticationType
import com.example.mystybag.biometricUtils.helpers.BiometricErrorResult
import com.example.mystybag.common.logic.NavigatorMerchantBiometric
import com.example.mystybag.core.expected.LogHelper
import com.example.mystybag.login.model.MerchantBiometricUIState
import com.example.mystybag.sharedmodels.defines.MerchantStatusDefines.Companion.MERCHANT_STATUS_UNDER_REVIEW
import com.example.mystybag.sharedmodels.project.requestModels.merchant.GetMerchantDashboardRequestModel
import com.example.mystybag.sharedmodels.project.requestModels.merchant.LoginMerchantRequestModel
import com.example.mystybag.sharedmodels.project.responseModels.merchant.GetMerchantDashboardResponseModel
import kotlinx.coroutines.launch

class MerchantBiometricViewModel(
    context: AppContext,
    private val config: com.example.mystybag.core.logic.base.NavigatorConfig? = null
) : ChildViewModel<MerchantBiometricUIState>(context, MerchantBiometricUIState()), LoginInterface {

    /** Password to save only after user enables biometric and passes auth (passed from MerchantLoginViewModel). */
    private val passwordToSaveAfterBiometric: String? =
        (config as? NavigatorMerchantBiometric)?.passwordToSaveAfterBiometric
    /** When coming from login, session is passed in config so we do not depend on Preferences/isJwtExpired (which can fail on iOS). */
    private val sessionFromLogin: Pair<String, Long>? = (config as? NavigatorMerchantBiometric)?.let { n ->
        if (n.jwt.isNotEmpty() && n.merchantId != 0L) Pair(n.jwt, n.merchantId) else null
    }
    
    override fun onCreate() {
        navControls.backButtonVisible = false
        
        // Check if biometric is available
        val biometricType = BiometricsHandler.getAuthenticationType()
        updateState { it.copy(biometricType = biometricType) }
        
        val biometricAvailable = biometricType != BiometricAuthenticationType.NONE && BiometricsHandler.isBiometricScannerAvailableAndSet()
        
        // When we just came from login (session in config or password to save), handle first. Do NOT run isJwtExpired(Preferences) here —
        // on iOS it can incorrectly return true and send the user back to login before they see the biometric screen.
        if (sessionFromLogin != null || passwordToSaveAfterBiometric != null) {
            if (biometricAvailable) {
                updateState { it.copy(showEnableScreen = true) }
            } else {
                onSkipBiometric()
            }
            return
        }
        
        // Check if biometric is already enabled for merchant
        val useBiometricForMerchant = Preferences.getBoolean(key = PREFERENCES_USE_BIOMETRIC_FOR_MERCHANT, false)
        val merchantJwt = Preferences.getString(key = PREFERENCES_MERCHANT_JWT, "")
        
        if (merchantJwt.isNotEmpty() && isJwtExpired(merchantJwt)) {
            Preferences.setString(PREFERENCES_MERCHANT_JWT, "")
            launchOnMain { onBackButtonClicked() }
            return
        }
        
        if (merchantJwt.isNotEmpty() && useBiometricForMerchant) {
            // Merchant is already logged in and biometric is enabled - start authentication
            if (biometricAvailable) {
                startBiometricAuthentication()
            } else {
                onSkipBiometric()
            }
            return
        }
        
        // Token expired re-login: no JWT but we have saved credentials + biometric -> verify biometric and login with email/password
        if (merchantJwt.isEmpty() && useBiometricForMerchant && biometricAvailable) {
            val storedEmail = Preferences.getString(key = PREFERENCES_MERCHANT_EMAIL, "")
            val storedPassword = Preferences.getSecureString(key = PREFERENCES_MERCHANT_PASSWORD, defaultValue = "")
            if (storedEmail.isNotEmpty() && storedPassword.isNotEmpty()) {
                startBiometricAuthentication()
                return
            }
            launchOnMain { onBackButtonClicked() }
            return
        }
        
        // First time setup - show full-screen "Enable biometric" UI (Figma)
        if (biometricAvailable) {
            updateState { it.copy(showEnableScreen = true) }
        } else {
            onSkipBiometric()
        }
    }
    
    /** Called when user taps "Set up" on the Enable biometric screen. */
    fun onSetUpBiometric() {
        enableBiometric()
    }
    
    private fun startBiometricAuthentication() {
        val started = BiometricsHandler.startBiometricAuthentication(
            activity = sharedActivity,
            onSuccess = {
                // Biometric success: call merchant login, then save and go to main.
                performLoginAfterBiometric()
            },
            onFailed = { error ->
                when (error) {
                    BiometricErrorResult.USER_CANCELED -> {
                        // User canceled, stay on this screen or go back
                        // For now, we'll go back to welcome screen
                        onBackButtonClicked()
                    }
                    else -> {
                        showSnackbar(sharedString(SR.strings.an_error_occurred_please_try_again_later))
                        // On error, go back to welcome screen
                        onBackButtonClicked()
                    }
                }
            }
        )

        // If prompt couldn't be started (e.g., missing keystore key), don't leave the user stuck here.
        if (!started) {
            onSkipBiometric()
        }
    }
    
    private fun enableBiometric() {
        launchOnMain {
            BiometricsHandler.startSettingBiometricAuthentication(
                activity = sharedActivity,
                onSuccess = {
                    BiometricsHandler.storeFinalKey()
                    Preferences.setBoolean(PREFERENCES_USE_BIOMETRIC_FOR_MERCHANT, true)
                    // Save merchant password only after permission and successful biometric (requirement).
                    passwordToSaveAfterBiometric?.let { pwd ->
                        try {
                            Preferences.setSecureString(PREFERENCES_MERCHANT_PASSWORD, pwd)
                        } catch (e: Throwable) {
                            LogHelper.logEvent("[MerchantBiometric] setSecureString(password) failed: ${e.message}")
                        }
                    }
                    onSkipBiometric()
                },
                onFailed = { error ->
                    when (error) {
                        BiometricErrorResult.USER_CANCELED -> {
                            onSkipBiometric()
                        }
                        else -> {
                            showSnackbar(sharedString(SR.strings.an_error_occurred_please_try_again_later))
                            onSkipBiometric()
                        }
                    }
                }
            )
        }
    }
    
    /** Called after biometric success: call merchant login API, save JWT/prefs, then go to main. */
    private fun performLoginAfterBiometric() {
        val storedEmail = Preferences.getString(key = PREFERENCES_MERCHANT_EMAIL, "")
        val storedPassword = Preferences.getSecureString(key = PREFERENCES_MERCHANT_PASSWORD, defaultValue = "")
        if (storedEmail.isEmpty() || storedPassword.isEmpty()) {
            launchOnMain {
                showSnackbar(sharedString(SR.strings.please_log_in_again))
                onBackButtonClicked()
            }
            return
        }
        scope.launch {
            launchOnMain { updateState { it.copy(isLoading = true) } }
            commandBlock(showProgress = true) {
                CommonCommands.loginMerchant(
                    LoginMerchantRequestModel(email = storedEmail, password = Security.hashPasswordForRequest(storedPassword))
                )
                    .onResponse(
                        success = { response ->
                            launchOnMain {
                                updateState { it.copy(isLoading = false) }
                                val loginResponse = response as? com.example.mystybag.sharedmodels.project.responseModels.merchant.LoginMerchantResponseModel
                                if (loginResponse != null) {
                                    if (loginResponse.merchantStatus == MERCHANT_STATUS_UNDER_REVIEW) {
                                        replaceAll(NavigatorMerchantRegistrationUnderReview)
                                        return@launchOnMain
                                    }
                                    Preferences.setString(PREFERENCES_MERCHANT_JWT, loginResponse.jwt)
                                    Preferences.setLong(PREFERENCES_MERCHANT_ID, loginResponse.merchantId)
                                    Preferences.setBoolean(PREFERENCES_IS_STAFF, false)
                                    Preferences.setString(PREFERENCES_STAFF_LOCATION_IDS, "")
                                    Communication.token = loginResponse.jwt
                                    decideMerchantDestinationAndGo(loginResponse.merchantId)
                                } else {
                                    showSnackbar(sharedString(SR.strings.please_log_in_again))
                                    onBackButtonClicked()
                                }
                            }
                        },
                        baseError = { err ->
                            launchOnMain {
                                updateState { it.copy(isLoading = false) }
                                showSnackbar(Response.getErrorMessage(err.status, err.apiEndPoint, err.statusMessage))
                                onBackButtonClicked()
                            }
                        }
                    )
            }
        }
    }

    fun onSkipBiometric() {
        launchOnMain {
            // Show biometric offer only once; do not show again after user skips.
            Preferences.setBoolean(PREFERENCES_MERCHANT_BIOMETRIC_PROMPT_DECLINED, true)
            val (jwt, merchantId) = when {
                sessionFromLogin != null -> sessionFromLogin!!.first to sessionFromLogin!!.second
                else -> {
                    val prefsJwt = Preferences.getString(key = PREFERENCES_MERCHANT_JWT, "")
                    if (prefsJwt.isNotEmpty() && isJwtExpired(prefsJwt)) {
                        Preferences.setString(PREFERENCES_MERCHANT_JWT, "")
                        onBackButtonClicked()
                        return@launchOnMain
                    }
                    prefsJwt to Preferences.getLong(key = PREFERENCES_MERCHANT_ID, 0L)
                }
            }
            if (jwt.isNotEmpty()) {
                Communication.token = jwt
            }
            if (merchantId != 0L) {
                decideMerchantDestinationAndGo(merchantId)
            } else {
                setResult<LoginInterface> { goToMainScreen(false) }
            }
        }
    }

    /** If merchant has no locations, go directly to AddLocationPrompt; otherwise go to main (Dashboard). */
    private fun decideMerchantDestinationAndGo(merchantId: Long) {
        scope.launch {
            commandBlock(showProgress = false, hideProgress = false) {
                CommonCommands.getMerchantDashboard(GetMerchantDashboardRequestModel(merchantId = merchantId))
                    .onResponse(
                        success = { response ->
                            launchOnMain {
                                val dashboard = response as? GetMerchantDashboardResponseModel
                                val locations = dashboard?.locations ?: emptyList()
                                if (locations.isEmpty()) {
                                    setResult<LoginInterface> {
                                        goToMainScreen(
                                            false,
                                            null,
                                            NavigatorAddLocationPrompt(
                                                merchantId = merchantId,
                                                companyPhoneNumber = dashboard?.companyPhoneNumber,
                                                companyDialCode = dashboard?.companyDialCode,
                                                registeredCompanyStreet = dashboard?.registeredCompanyStreet,
                                                registeredCompanyCityId = dashboard?.registeredCompanyCityId,
                                                registeredCompanyCityName = dashboard?.registeredCompanyCityName
                                            )
                                        )
                                    }
                                } else {
                                    setResult<LoginInterface> { goToMainScreen(false) }
                                }
                            }
                        },
                        baseError = { _ ->
                            launchOnMain { setResult<LoginInterface> { goToMainScreen(false) } }
                        }
                    )
            }
        }
    }
    
    override fun goToMainScreen(showProgress: Boolean, onErrorCallback: (() -> Unit)?, target: NavigatorConfig?) {
        setResult<LoginInterface> { goToMainScreen(showProgress, onErrorCallback, target) }
    }
}

